import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDebounce } from './useDebounce';
import { searchJobs, SearchFilters, JobSearchResult } from '@/utils/optimizedQueries';

export const useInstantSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Extract filters from URL
  const filters: SearchFilters = useMemo(() => ({
    query: searchParams.get('q') || '',
    location: searchParams.get('location') || 'all',
    job_type: searchParams.get('type') || 'all',
    experience_level: searchParams.get('level') || 'all',
    is_remote: searchParams.get('remote') === 'true',
    salary_min: searchParams.get('salary') ? parseInt(searchParams.get('salary')!) : undefined,
    sort_by: (searchParams.get('sort') as 'relevance' | 'newest' | 'salary') || 'relevance',
  }), [searchParams]);

  // Debounced search query
  const debouncedQuery = useDebounce(filters.query, 150);
  
  // Search state
  const [jobs, setJobs] = useState<JobSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  // Update URL parameters
  const updateFilters = (newFilters: Partial<SearchFilters>) => {
    const updatedParams = new URLSearchParams(searchParams);
    
    // Update each filter
    if (newFilters.query !== undefined) {
      if (newFilters.query) {
        updatedParams.set('q', newFilters.query);
      } else {
        updatedParams.delete('q');
      }
    }
    
    if (newFilters.location !== undefined) {
      if (newFilters.location && newFilters.location !== 'all') {
        updatedParams.set('location', newFilters.location);
      } else {
        updatedParams.delete('location');
      }
    }
    
    if (newFilters.job_type !== undefined) {
      if (newFilters.job_type && newFilters.job_type !== 'all') {
        updatedParams.set('type', newFilters.job_type);
      } else {
        updatedParams.delete('type');
      }
    }
    
    if (newFilters.experience_level !== undefined) {
      if (newFilters.experience_level && newFilters.experience_level !== 'all') {
        updatedParams.set('level', newFilters.experience_level);
      } else {
        updatedParams.delete('level');
      }
    }
    
    if (newFilters.is_remote !== undefined) {
      if (newFilters.is_remote) {
        updatedParams.set('remote', 'true');
      } else {
        updatedParams.delete('remote');
      }
    }
    
    if (newFilters.salary_min !== undefined) {
      if (newFilters.salary_min && newFilters.salary_min > 0) {
        updatedParams.set('salary', newFilters.salary_min.toString());
      } else {
        updatedParams.delete('salary');
      }
    }
    
    if (newFilters.sort_by !== undefined) {
      if (newFilters.sort_by && newFilters.sort_by !== 'relevance') {
        updatedParams.set('sort', newFilters.sort_by);
      } else {
        updatedParams.delete('sort');
      }
    }

    setSearchParams(updatedParams);
    setPage(0); // Reset pagination when filters change
  };

  // Remove specific filter
  const removeFilter = (filterKey: keyof SearchFilters) => {
    const updatedParams = new URLSearchParams(searchParams);
    
    // Directly remove the URL parameter for the specific filter
    switch (filterKey) {
      case 'query':
        updatedParams.delete('q');
        break;
      case 'location':
        updatedParams.delete('location');
        break;
      case 'job_type':
        updatedParams.delete('type');
        break;
      case 'experience_level':
        updatedParams.delete('level');
        break;
      case 'is_remote':
        updatedParams.delete('remote');
        break;
      case 'salary_min':
        updatedParams.delete('salary');
        break;
      case 'sort_by':
        updatedParams.delete('sort');
        break;
    }
    
    setSearchParams(updatedParams);
    setPage(0);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchParams(new URLSearchParams());
    setPage(0);
  };

  // Get active filters for chips
  const activeFilters = useMemo(() => {
    const active: Array<{ key: keyof SearchFilters; label: string; value: any }> = [];
    
    if (filters.query) {
      active.push({ key: 'query', label: `Search: "${filters.query}"`, value: filters.query });
    }
    
    if (filters.location && filters.location !== 'all') {
      active.push({ key: 'location', label: `Location: ${filters.location}`, value: filters.location });
    }
    
    if (filters.job_type && filters.job_type !== 'all') {
      active.push({ key: 'job_type', label: `Type: ${filters.job_type}`, value: filters.job_type });
    }
    
    if (filters.experience_level && filters.experience_level !== 'all') {
      active.push({ key: 'experience_level', label: `Level: ${filters.experience_level}`, value: filters.experience_level });
    }
    
    if (filters.is_remote) {
      active.push({ key: 'is_remote', label: 'Remote', value: filters.is_remote });
    }
    
    if (filters.salary_min) {
      active.push({ key: 'salary_min', label: `Min Salary: ${filters.salary_min}`, value: filters.salary_min });
    }
    
    return active;
  }, [filters]);

  // Perform search
  const performSearch = async (loadMore = false) => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const currentPage = loadMore ? page : 0;
      const offset = currentPage * 20;
      
      // Create search filters with debounced query
      const searchFilters = {
        ...filters,
        query: debouncedQuery,
      };
      
      const { data, error: searchError } = await searchJobs(searchFilters, 20, offset);
      
      if (searchError) {
        setError('Failed to search jobs');
        return;
      }
      
      if (loadMore) {
        setJobs(prev => [...prev, ...(data || [])]);
      } else {
        setJobs(data || []);
      }
      
      setHasMore((data?.length || 0) === 20);
      
      if (loadMore) {
        setPage(prev => prev + 1);
      }
    } catch (err) {
      setError('An error occurred while searching');
    } finally {
      setIsLoading(false);
    }
  };

  // Load more results
  const loadMore = () => {
    if (hasMore && !isLoading) {
      performSearch(true);
    }
  };

  // Search when debounced query or other filters change
  useEffect(() => {
    performSearch();
  }, [debouncedQuery, filters.location, filters.job_type, filters.experience_level, filters.is_remote, filters.salary_min, filters.sort_by]);

  return {
    filters,
    updateFilters,
    removeFilter,
    clearAllFilters,
    activeFilters,
    jobs,
    isLoading,
    error,
    hasMore,
    loadMore,
    performSearch: () => performSearch(false),
  };
};